// web/src/player/slideShow.ts
import { buildZoomWindows } from "./smartZoom";
import type { DetectedObject, WordTiming, SlideScheduleEntry, SegmentTiming } from "../types/roast";

export function buildSlideZoomWindows(
  regions: DetectedObject[], words: WordTiming[], dwellStart: number, dwellEnd: number,
) {
  return buildZoomWindows(regions, words)
    .map((w) => ({ ...w, startSec: Math.max(w.startSec, dwellStart), endSec: Math.min(w.endSec, dwellEnd) }))
    .filter((w) => w.endSec > w.startSec);
}

/** Words whose start falls inside a dwell's time span — scopes deck zoom-phrase matching to
 *  THIS slide's spoken clip (so a phrase repeated elsewhere can't resolve out of the dwell). */
export function wordsInDwell(words: WordTiming[], startSec: number, endSec: number): WordTiming[] {
  return words.filter((w) => w.start >= startSec && w.start < endSec);
}

export function getCurrentSlide(t: number, schedule: SlideScheduleEntry[]): SlideScheduleEntry | null {
  if (schedule.length === 0) return null;
  const hit = schedule.find((e) => t >= e.startSec && t < e.endSec);
  return hit ?? schedule[schedule.length - 1];
}

/** Select the schedule entry for an audible word index. Mirrors getCurrentSlide's
 *  tail fallback, but clamps a below-range index (e.g. -1 before the first spoken
 *  word) to the FIRST entry (the title) rather than the last. */
export function getSlideForWord(idx: number, schedule: SlideScheduleEntry[]): SlideScheduleEntry | null {
  if (schedule.length === 0) return null;
  const first = schedule[0];
  if (first.startWord !== undefined && idx < first.startWord) return first;
  const hit = schedule.find(
    (e) => e.startWord !== undefined && e.endWord !== undefined && idx >= e.startWord && idx < e.endWord,
  );
  return hit ?? schedule[schedule.length - 1];
}

export const DEFAULT_MIN_SWEEP_DWELL_SEC = 0.25;

/** Evenly sample `count` items from `arr` (count < arr.length), always including the
 *  first and last, ascending, deduped — mirrors the retired planRiffleCoverage sampler. */
function sampleEvenly<T>(arr: T[], count: number): T[] {
  if (count >= arr.length) return arr.slice();
  if (count <= 1) return [arr[0]];
  const out: T[] = [];
  const seen = new Set<number>();
  for (let s = 0; s < count; s++) {
    const idx = Math.round((arr.length - 1) * (s / (count - 1)));
    if (!seen.has(idx)) { seen.add(idx); out.push(arr[idx]); }
  }
  return out;
}

/** Deterministic deck schedule from clip timings (the structural-sync core).
 *  - tentpole segment (1 slideIndex) → one kind:'dwell' entry over [clipStartSec, clipEndSec].
 *  - sweep segment (N slideIndices) → kind:'riffle' entries evenly sub-dividing the clip span,
 *    each >= minSweepDwellSec (~0.25s); if the realized run can't fit at the min, sample a
 *    representative subset (logging what was dropped) and re-divide the full clip span.
 *  Every entry's slide is intersected with the realized rasters (the blank-frame guard); a
 *  tentpole/whole-sweep that didn't render holds the previous slide. Entries are contiguous,
 *  carry NO startWord (deck routes the player TIME path; within-dwell smart zoom runs off this
 *  time path — see the RoastPlayer deck branch), and result[0].slideIndex === 0. */
export function buildDeckSchedule(
  segmentTimings: SegmentTiming[],
  realizedSlideIndices: number[],
  opts?: { minSweepDwellSec?: number },
): SlideScheduleEntry[] {
  const minDwell = opts?.minSweepDwellSec ?? DEFAULT_MIN_SWEEP_DWELL_SEC;
  const realized = new Set(realizedSlideIndices);
  const out: SlideScheduleEntry[] = [];
  let prevSlide = 0;

  for (const seg of segmentTimings) {
    const start = seg.clipStartSec;
    const end = seg.clipEndSec;
    if (end <= start) continue;
    const run = seg.slideIndices.filter((i) => realized.has(i));

    if (seg.slideIndices.length <= 1) {
      const slide = run.length ? run[0] : prevSlide;
      out.push({ slideIndex: slide, startSec: start, endSec: end, kind: "dwell" });
      prevSlide = slide;
      continue;
    }

    if (run.length === 0) {
      out.push({ slideIndex: prevSlide, startSec: start, endSec: end, kind: "riffle" });
      continue;
    }

    const clipDur = end - start;
    const maxFrames = Math.max(1, Math.floor(clipDur / minDwell));
    let frames = run;
    if (run.length > maxFrames) {
      frames = sampleEvenly(run, maxFrames);
      console.warn("[deck-schedule] sweep run too long for the min dwell — sampled a subset", {
        clipStartSec: start, clipEndSec: end, minSweepDwellSec: minDwell,
        runLength: run.length, kept: frames.length, dropped: run.length - frames.length,
        droppedSlides: run.filter((i) => !frames.includes(i)),
      });
    }
    const n = frames.length;
    frames.forEach((slide, k) => {
      const s = start + (clipDur * k) / n;
      const e = k === n - 1 ? end : start + (clipDur * (k + 1)) / n;
      out.push({ slideIndex: slide, startSec: s, endSec: e, kind: "riffle" });
      prevSlide = slide;
    });
  }

  return out.filter((e) => e.endSec > e.startSec);
}
